import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Search, ScrollText, Trash2, RotateCcw, User, ShieldAlert, Filter } from "lucide-react";

const ACTION_COLORS = {
  delete: "bg-[#ff3b5c20] text-[#ff3b5c] border-[#ff3b5c40]",
  bulk_delete: "bg-[#ff3b5c20] text-[#ff3b5c] border-[#ff3b5c40]",
  restore: "bg-[#ff000022] text-[color:var(--accent)] border-[#ff000044]",
  client_error: "bg-[#ff3b5c20] text-[#ff3b5c] border-[#ff3b5c40]",
};

const KIND_ICON = { user: User, incident: ShieldAlert, frontend: ShieldAlert };

function rel(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [action, setAction] = useState("all");
  const [kind, setKind] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (action !== "all") params.action = action;
    if (kind !== "all") params.target_kind = kind;
    params.limit = 300;
    const { data } = await api.get("/admin/audit-log", { params });
    setRows(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, [action, kind]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((r) =>
      [r.actor_name, r.actor_id, r.target_id, r.action, r.target_kind, JSON.stringify(r.meta || {})]
        .some((f) => (f || "").toString().toLowerCase().includes(query))
    );
  }, [rows, q]);

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div className="flex items-center gap-3">
        <ScrollText size={22} className="text-[color:var(--accent)]" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Audit Log</h1>
          <p className="text-sm text-slate-400">Every delete + restore across users and incidents, freshest first.</p>
        </div>
      </div>

      <div className="card-surface p-4 flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="audit-search"
            placeholder="Search actor, target, or metadata…"
            className="w-full pl-9 pr-3 py-2 bg-[#0b0d12] border border-[#2a3447] rounded-lg outline-none focus:border-[color:var(--accent)] text-sm"
          />
        </div>
        <select value={action} onChange={(e) => setAction(e.target.value)} data-testid="audit-action" className="px-3 py-2 bg-[#0b0d12] border border-[#2a3447] rounded-lg text-sm">
          <option value="all">All actions</option>
          <option value="delete">delete</option>
          <option value="bulk_delete">bulk_delete</option>
          <option value="restore">restore</option>
          <option value="client_error">client_error</option>
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="audit-kind" className="px-3 py-2 bg-[#0b0d12] border border-[#2a3447] rounded-lg text-sm">
          <option value="all">All targets</option>
          <option value="user">user</option>
          <option value="incident">incident</option>
          <option value="frontend">frontend</option>
        </select>
        <div className="text-xs text-slate-500 flex items-center gap-1"><Filter size={12} /> {filtered.length} of {rows.length}</div>
      </div>

      <div className="card-surface overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0b0d12] border-b border-[#2a3447] text-slate-400 uppercase text-xs tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">When</th>
              <th className="text-left px-4 py-3">Action</th>
              <th className="text-left px-4 py-3">Target</th>
              <th className="text-left px-4 py-3">Actor</th>
              <th className="text-left px-4 py-3">Details</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">IP</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const Icon = KIND_ICON[r.target_kind] || ScrollText;
              return (
                <tr key={r.id} data-testid={`audit-row-${r.id}`} className="border-b border-[#1a202c] hover:bg-[#121620] align-top">
                  <td className="px-4 py-3 text-slate-300 text-xs whitespace-nowrap" title={r.created_at}>{rel(r.created_at)}</td>
                  <td className="px-4 py-3">
                    <span className={`pill border ${ACTION_COLORS[r.action] || ""}`}>
                      {r.action === "restore" ? <RotateCcw size={11} className="inline mr-1" /> : <Trash2 size={11} className="inline mr-1" />}
                      {r.action}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 text-slate-200">
                      <Icon size={13} /> <span className="text-xs">{r.target_kind}</span>
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono truncate max-w-[180px]">{r.target_id}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-slate-200 text-sm">{r.actor_name || "—"}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">{r.actor_role}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400 max-w-[280px]">
                    {r.meta && Object.keys(r.meta).length > 0
                      ? Object.entries(r.meta).map(([k, v]) => <div key={k}><span className="text-slate-500">{k}:</span> {String(v)}</div>)
                      : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs hidden md:table-cell">{r.ip || "—"}</td>
                </tr>
              );
            })}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-slate-500 text-sm">
                <ScrollText size={24} className="mx-auto mb-2 opacity-40" /> No audit entries match your filters.
              </td></tr>
            )}
          </tbody>
        </table>
        {loading && <div className="text-slate-500 py-8 text-center text-sm">Loading…</div>}
      </div>
    </div>
  );
}
